/**
 * Всё, что приложение хранит в localStorage, — в одном месте: на каждую запись свой метод
 * (`getAuthData`, `setAuthData` и т. д.), ключи наружу не выходят.
 *
 * Хранилище может быть недоступно (приватный режим, запрет cookies, переполнена квота) —
 * поэтому методы не бросают исключений: чтение возвращает null или значение по умолчанию,
 * запись — false. Хранилища zustand `persist` задают свои ключи в `name` и сюда не входят.
 */

const KEYS = {
	authAccessToken: "grid-planner:auth-access-token",
	authRefreshToken: "grid-planner:auth-refresh-token",
	lastProjects: "grid-planner:last-projects",
	/** Проект, сохранённый кнопкой «Сохранить» без сервера */
	localProject: "grid-planner:local-project",
	/** Картинки подложек по id — отдельно от проекта, они тяжёлые */
	underlayImages: "grid-planner:underlay-images",
	/** Левая панель редактора плана широкая (с подписями) или узкая (только иконки) */
	isPlannerLeftPanelWide: "grid-planner:is-planner-left-panel-wide",
	/** Окно «Оставьте отзыв»: показано ли и сколько времени пользователь провёл до него */
	feedbackPrompt: "grid-planner:feedback-prompt",
} as const;

type Key = (typeof KEYS)[keyof typeof KEYS];

/** Пара токенов вошедшего пользователя. */
export interface AuthData {
	accessToken: string;
	refreshToken: string;
}

/** Картинки подложек: id подложки → data URL. */
export type UnderlayImagesById = Record<string, string>;

/** Окно «Оставьте отзыв» всплывает само, когда пользователь проведёт в приложении достаточно времени. */
export interface FeedbackPromptState {
	wasShown: boolean;
	/** Сколько миллисекунд пользователь провёл в приложении, пока окно ещё не показывалось */
	timeSpentMs: number;
}

const read = (key: Key): string | null => {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
};

const write = (key: Key, value: string): boolean => {
	try {
		localStorage.setItem(key, value);
		return true;
	} catch {
		return false;
	}
};

const remove = (key: Key): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		// Хранилище недоступно — удалять нечего
	}
};

const readJson = (key: Key): unknown => {
	const raw = read(key);
	if (raw === null) return null;
	try {
		return JSON.parse(raw) as unknown;
	} catch {
		return null;
	}
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

// Авторизация

/** Токены входа; null — пользователь не входил или пара неполная. */
const getAuthData = (): AuthData | null => {
	const accessToken = read(KEYS.authAccessToken);
	const refreshToken = read(KEYS.authRefreshToken);
	return accessToken === null || refreshToken === null ? null : { accessToken, refreshToken };
};

const setAuthData = ({ accessToken, refreshToken }: AuthData): boolean =>
	write(KEYS.authAccessToken, accessToken) && write(KEYS.authRefreshToken, refreshToken);

const clearAuthData = (): void => {
	remove(KEYS.authAccessToken);
	remove(KEYS.authRefreshToken);
};

const getLastProject = (userId: string): string | null => {
	const projects = readJson(KEYS.lastProjects);
	return isRecord(projects) && typeof projects[userId] === "string" ? projects[userId] : null;
};

const setLastProject = (userId: string, projectId: string): boolean => {
	const projects = readJson(KEYS.lastProjects);
	return write(
		KEYS.lastProjects,
		JSON.stringify({ ...(isRecord(projects) ? projects : {}), [userId]: projectId }),
	);
};

const clearLastProject = (userId: string): void => {
	const projects = readJson(KEYS.lastProjects);
	if (!isRecord(projects)) return;
	delete projects[userId];
	write(KEYS.lastProjects, JSON.stringify(projects));
};

// Проект, сохранённый без сервера (сериализованный файл проекта)

const getLocalProject = (): string | null => read(KEYS.localProject);

/** false — проект не поместился в квоту или хранилище недоступно. */
const setLocalProject = (serialized: string): boolean => write(KEYS.localProject, serialized);

const hasLocalProject = (): boolean => getLocalProject() !== null;

// Картинки подложек

const getUnderlayImages = (): UnderlayImagesById => {
	const parsed = readJson(KEYS.underlayImages);
	return isRecord(parsed) ? (parsed as UnderlayImagesById) : {};
};

/** false — не поместилось в квоту: вызывающий отбрасывает старые картинки и пробует снова. */
const setUnderlayImages = (images: UnderlayImagesById): boolean =>
	write(KEYS.underlayImages, JSON.stringify(images));

// Ширина левой панели редактора плана

/** По умолчанию панель широкая. */
const isPlannerLeftPanelWide = (): boolean => read(KEYS.isPlannerLeftPanelWide) !== "false";

const setPlannerLeftPanelWide = (wide: boolean): boolean =>
	write(KEYS.isPlannerLeftPanelWide, String(wide));

// Окно «Оставьте отзыв»

const getFeedbackPrompt = (): FeedbackPromptState => {
	const state = readJson(KEYS.feedbackPrompt);
	if (!isRecord(state)) return { wasShown: false, timeSpentMs: 0 };
	return {
		wasShown: state.wasShown === true,
		timeSpentMs: typeof state.timeSpentMs === "number" ? state.timeSpentMs : 0,
	};
};

const setFeedbackPrompt = (state: FeedbackPromptState): boolean =>
	write(KEYS.feedbackPrompt, JSON.stringify(state));

export const LocalStorageUtils = {
	getAuthData,
	setAuthData,
	clearAuthData,
	getLastProject,
	setLastProject,
	clearLastProject,
	getLocalProject,
	setLocalProject,
	hasLocalProject,
	getUnderlayImages,
	setUnderlayImages,
	isPlannerLeftPanelWide,
	setPlannerLeftPanelWide,
	getFeedbackPrompt,
	setFeedbackPrompt,
};
